import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getEventById, getTicketTypes } from '../services/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DirectionsIcon from '@mui/icons-material/Directions';
import AuthModal from '../components/AuthModal';
import FeeInfoButton from '../components/FeeInfoButton';
import RecintoMap from '../components/RecintoMap';
import TicketTypeSelector from '../components/TicketTypeSelector';
import useWindowSize from '../hooks/useWindowSize';

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  return match ? match[1] : null;
};

function Event() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareUrl, setShowShareUrl] = useState(false);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [videoPage, setVideoPage] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('entradas');
  const [ticketTypes, setTicketTypes] = useState([]);
  const [busCapacity, setBusCapacity] = useState(null);
  const [hasTicketTypes, setHasTicketTypes] = useState(false);
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const buyRef = useRef(null);
  const descRef = useRef(null);
  const videosRef = useRef(null);
  const mapRef = useRef(null);
  const recintoRef = useRef(null);

  useEffect(() => {
    getEventById(id)
      .then(res => {
        setEvent(res.data);
        setLoading(false);
        return getTicketTypes(res.data.id);
      })
      .then(res => {
        if (res.data.ticket_types && res.data.ticket_types.length > 0) {
          setTicketTypes(res.data.ticket_types);
          setBusCapacity(res.data.bus_capacity);
          setHasTicketTypes(true);
        }
      })
      .catch(() => setLoading(false));
  }, [id]);

  const scrollToSection = (ref, tabId) => {
    setActiveTab(tabId);
    if (!ref.current) return;
    const offset = 120;
    const top = ref.current.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: event.name, url });
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => setShowShareUrl(true));
    } else {
      setShowShareUrl(true);
    }
  };

  const handleBuy = () => {
    if (!event || event.available_tickets === 0) return;
    const user = localStorage.getItem('user');
    if (!user) setShowAuthModal(true);
    else navigate(`/checkout/${event.id}`);
  };

  const handleBuyByType = (ticketType, quantity) => {
    navigate(`/checkout/${event.id}`, {
      state: { ticketType, quantity }
    });
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Cargando...</p>
    </div>
  );

  if (!event) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Evento no encontrado</p>
    </div>
  );

  const eventImage = event.image || 'https://galiticket.com/logo.png';
  const eventSlug = event.slug || id;
  const eventDate = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const eventTime = new Date(event.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const videos = [event.video1, event.video2, event.video3, event.video4, event.video5, event.video6].filter(Boolean);
  const videoIds = videos.map(getYouTubeId).filter(Boolean);
  const perView = isMobile ? 1 : 3;
  const totalPages = Math.ceil(videoIds.length / perView);
  const visibleVideoIds = videoIds.slice(videoPage * perView, videoPage * perView + perView);
  const descNeedsTruncation = event.description && (event.description.length > 400 || event.description.split('\n').length > 14);

  const buyBtnStyle = {
    backgroundColor: event.available_tickets === 0 ? colors.bgTertiary : colors.accent,
    color: event.available_tickets === 0 ? colors.textSecondary : colors.accentText,
    border: 'none', borderRadius: '25px',
    cursor: event.available_tickets === 0 ? 'default' : 'pointer',
    fontWeight: 'bold', width: '100%',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  const SeatInfoBanner = ({ mobile }) => event.seat_info ? (
    <p style={{
      color: colors.textSecondary,
      fontSize: mobile ? '0.78rem' : '0.82rem',
      marginBottom: mobile ? '0.8rem' : '1rem',
      lineHeight: 1.5,
    }}>
      {event.seat_info}
    </p>
  ) : null;

  const tabs = [
    { id: 'entradas', label: 'Entradas', ref: buyRef },
    ...(event.description ? [{ id: 'descripcion', label: 'Descripción', ref: descRef }] : []),
    ...(videoIds.length > 0 ? [{ id: 'videos', label: 'Vídeos', ref: videosRef }] : []),
    ...(event.map_url ? [{ id: 'ubicacion', label: 'Ubicación', ref: mapRef }] : []),
    ...(event.venue_map ? [{ id: 'recinto', label: 'Mapa recinto', ref: recintoRef }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Helmet>
        <title>{event.name} - GaliTicket</title>
        <meta name="description" content={`Compra entradas para ${event.name}. ${eventDate} en ${event.location}. Precio desde ${event.price}€.`} />
        <meta property="og:title" content={`${event.name} - GaliTicket`} />
        <meta property="og:description" content={`Compra entradas para ${event.name}. ${eventDate} en ${event.location}. Precio desde ${event.price}€.`} />
        <meta property="og:image" content={eventImage} />
        <meta property="og:url" content={`https://galiticket.com/${eventSlug}`} />
        <meta property="og:type" content="event" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${event.name} - GaliTicket`} />
        <meta name="twitter:description" content={`Compra entradas para ${event.name}. ${eventDate} en ${event.location}.`} />
        <meta name="twitter:image" content={eventImage} />
        <link rel="canonical" href={`https://galiticket.com/${eventSlug}`} />
      </Helmet>

      <Navbar />

      {playingVideoId && (
        <div onClick={() => setPlayingVideoId(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '860px', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden' }}>
            <iframe src={`https://www.youtube.com/embed/${playingVideoId}?autoplay=1`} title="video" frameBorder="0" allowFullScreen allow="autoplay; encrypted-media" style={{ width: '100%', height: '100%' }} />
          </div>
          <button onClick={() => setPlayingVideoId(null)} style={{ position: 'fixed', top: '1rem', right: '1rem', backgroundColor: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
        </div>
      )}

      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => { setShowAuthModal(false); navigate(`/checkout/${event.id}`); }}
        />
      )}

      {isMobile && event.banner_image && (
        <div style={{ width: '100%', height: '220px', overflow: 'hidden' }}>
          <img src={event.banner_image} alt={event.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
        </div>
      )}

      <div style={{ position: 'sticky', top: '60px', zIndex: 100, backgroundColor: colors.bg, borderBottom: `1px solid ${colors.border}`, overflowX: 'auto' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '0 1rem' : '0 2rem', display: 'flex', gap: '0' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => scrollToSection(tab.ref, tab.id)}
              style={{
                backgroundColor: 'transparent', border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${colors.text}` : '2px solid transparent',
                color: activeTab === tab.id ? colors.text : colors.textSecondary,
                padding: isMobile ? '0.8rem 1rem' : '1rem 1.5rem',
                cursor: 'pointer', fontWeight: 'bold',
                fontSize: isMobile ? '0.78rem' : '0.85rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                whiteSpace: 'nowrap', marginBottom: '-1px',
                transition: 'color 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1100px', margin: '0 auto' }}>

        <button style={{ backgroundColor: 'transparent', border: 'none', color: colors.textSecondary, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '1.2rem' }} onClick={() => navigate('/')}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Volver
        </button>

        {isMobile ? (
          <div>
            <span style={{ backgroundColor: colors.accent, color: colors.accentText, padding: '0.2rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold' }}>{event.category || 'Evento'}</span>
            <h1 style={{ color: colors.text, fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '0.4rem', marginTop: '0.6rem', lineHeight: 1.1 }}>{event.name}</h1>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CalendarTodayIcon style={{ fontSize: 14, color: colors.accent }} />
                <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: 0 }}>{eventDate} · {eventTime}h</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <LocationOnIcon style={{ fontSize: 14, color: colors.accent }} />
                <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: 0 }}>{event.location}</p>
              </div>
            </div>

            <div ref={buyRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '1rem', marginBottom: '1.2rem' }}>
                  <SeatInfoBanner mobile={true} />
              {hasTicketTypes ? (
                <TicketTypeSelector
                  eventId={event.id}
                  ticketTypes={ticketTypes}
                  busCapacity={busCapacity}
                  onBuy={handleBuyByType}
                />
              ) : (
                <>
                  <p style={{ color: colors.textSecondary, fontSize: '0.75rem', marginBottom: '0.2rem' }}>Precio por entrada</p>
                  <p style={{ color: colors.accent, fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '0.2rem' }}>
                    {event.price}€ <span style={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 'normal' }}>IVA incluido</span>
                  </p>
                  <p style={{ color: colors.textSecondary, fontSize: '0.75rem', marginBottom: '0.8rem' }}>+ {(event.price * 0.10).toFixed(2)}€ gasto de gestión <FeeInfoButton /></p>
                  <button style={{ ...buyBtnStyle, padding: '0.7rem', fontSize: '0.9rem' }} onClick={handleBuy} disabled={event.available_tickets === 0}>
                    {event.available_tickets === 0 ? 'Agotado' : 'Entradas'}
                  </button>
                </>
              )}
            </div>

            {event.description && (
              <div ref={descRef} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
                <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Descripción del evento</p>
                <div style={{ position: 'relative' }}>
                  <p style={{ color: colors.textSecondary, fontSize: '0.82rem', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', maxHeight: descExpanded ? 'none' : '22rem', overflow: 'hidden' }}>
                    {event.description}
                  </p>
                  {!descExpanded && descNeedsTruncation && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4rem', background: `linear-gradient(to bottom, transparent, ${colors.bg})` }} />
                  )}
                </div>
                {descNeedsTruncation && (
                  <button onClick={() => setDescExpanded(!descExpanded)} style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.accent, borderRadius: '20px', padding: '0.4rem 1.2rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 'bold', marginTop: '0.8rem' }}>
                    {descExpanded ? 'Ver menos' : 'Saber más'}
                  </button>
                )}
              </div>
            )}

            {videoIds.length > 0 && (
              <div ref={videosRef} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                  <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem', margin: 0 }}>Galería de vídeos</p>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => setVideoPage(p => Math.max(0, p - 1))} disabled={videoPage === 0} style={{ backgroundColor: videoPage === 0 ? colors.bgTertiary : colors.accent, color: videoPage === 0 ? colors.textSecondary : colors.accentText, border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: videoPage === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ArrowBackIosIcon style={{ fontSize: 12 }} />
                      </button>
                      <button onClick={() => setVideoPage(p => Math.min(totalPages - 1, p + 1))} disabled={videoPage === totalPages - 1} style={{ backgroundColor: videoPage === totalPages - 1 ? colors.bgTertiary : colors.accent, color: videoPage === totalPages - 1 ? colors.textSecondary : colors.accentText, border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: videoPage === totalPages - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ArrowForwardIosIcon style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${perView}, 1fr)`, gap: '0.8rem' }}>
                  {visibleVideoIds.map((videoId, i) => (
                    <div key={i} onClick={() => setPlayingVideoId(videoId)} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', aspectRatio: '16/9', backgroundColor: '#000' }}>
                      <img src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} alt="video" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ backgroundColor: 'rgba(255,0,0,0.9)', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PlayArrowIcon style={{ color: '#fff', fontSize: 26 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {event.map_url && (
              <div ref={mapRef} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
                <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Ubicación</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                  <LocationOnIcon style={{ fontSize: 14, color: colors.accent }} />
                  <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: 0 }}>{event.location}</p>
                </div>
                <a href={event.map_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', backgroundColor: colors.accent, color: colors.accentText, padding: '0.6rem', borderRadius: '4px', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}>
                  <DirectionsIcon style={{ fontSize: 16 }} /> Cómo llegar
                </a>
              </div>
            )}

            {event.venue_map && (
              <div ref={recintoRef} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
                <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.8rem' }}>Mapa del recinto</p>
                <RecintoMap venueMap={event.venue_map} ticketTypes={ticketTypes} />
              </div>
            )}

            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
              <p style={{ color: colors.textSecondary, fontSize: '0.82rem', lineHeight: 1.6 }}>Se requiere presentar el código QR en la entrada del evento. Máximo 8 entradas por usuario.</p>
            </div>

            <button style={{ width: '100%', backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: copied ? '#00cc66' : colors.textSecondary, borderRadius: '4px', padding: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.85rem' }} onClick={handleShare}>
              <ShareIcon style={{ fontSize: 16 }} /> {copied ? '¡Enlace copiado!' : 'Compartir evento'}
            </button>
            {showShareUrl && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input readOnly value={window.location.href} onFocus={e => e.target.select()} style={{ flex: 1, backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.5rem', color: colors.text, fontSize: '0.8rem', outline: 'none' }} />
                <button onClick={() => setShowShareUrl(false)} style={{ backgroundColor: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}>✕</button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '3rem', alignItems: 'start' }}>

            <div style={{ position: 'sticky', top: '120px' }}>
              <div style={{ borderRadius: '16px', overflow: 'hidden', border: `1px solid ${colors.border}`, marginBottom: '1rem' }}>
                {event.image ? (
                  <img src={eventImage} alt={event.name} style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '2/3', backgroundColor: colors.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5rem' }}>🎵</div>
                )}
              </div>
              <button style={{ width: '100%', backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: copied ? '#00cc66' : colors.textSecondary, borderRadius: '25px', padding: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.9rem' }} onClick={handleShare}>
                <ShareIcon style={{ fontSize: 18 }} /> {copied ? '¡Enlace copiado!' : 'Compartir evento'}
              </button>
              {showShareUrl && (
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={window.location.href} onFocus={e => e.target.select()} style={{ flex: 1, backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.5rem', color: colors.text, fontSize: '0.8rem', outline: 'none' }} />
                  <button onClick={() => setShowShareUrl(false)} style={{ backgroundColor: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}>✕</button>
                </div>
              )}
            </div>

            <div>
              <span style={{ backgroundColor: colors.accent, color: colors.accentText, padding: '0.3rem 1rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>{event.category || 'Evento'}</span>
              <h1 style={{ color: colors.text, fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem', marginTop: '1rem', lineHeight: 1.05 }}>{event.name}</h1>
              <p style={{ color: colors.textSecondary, fontSize: '0.95rem', marginBottom: '1.5rem' }}>Organizado por GaliTicket</p>

              <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ color: colors.text, fontWeight: 'bold', marginBottom: '1rem', fontSize: '1rem' }}>Información del evento</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.6rem' }}>
                  <CalendarTodayIcon style={{ fontSize: 18, color: colors.accent, flexShrink: 0 }} />
                  <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>{eventDate} · {eventTime}h</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <LocationOnIcon style={{ fontSize: 18, color: colors.accent, flexShrink: 0 }} />
                  <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>{event.location}</p>
                </div>
              </div>

              <div ref={buyRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                    <SeatInfoBanner mobile={false} />
                {hasTicketTypes ? (
                  <TicketTypeSelector
                    eventId={event.id}
                    ticketTypes={ticketTypes}
                    busCapacity={busCapacity}
                    onBuy={handleBuyByType}
                  />
                ) : (
                  <>
                    <p style={{ color: colors.textSecondary, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Precio por entrada</p>
                    <p style={{ color: colors.accent, fontSize: '2.8rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                      {event.price}€ <span style={{ fontSize: '0.75rem', color: colors.textSecondary, fontWeight: 'normal' }}>IVA incluido</span>
                    </p>
                    <p style={{ color: colors.textSecondary, fontSize: '0.85rem', marginBottom: '1rem' }}>+ {(event.price * 0.10).toFixed(2)}€ gasto de gestión <FeeInfoButton size={16} /></p>
                    <button style={{ ...buyBtnStyle, padding: '1rem', fontSize: '1.1rem' }} onClick={handleBuy} disabled={event.available_tickets === 0}>
                      {event.available_tickets === 0 ? 'Entradas agotadas' : 'Comprar entradas'}
                    </button>
                  </>
                )}
              </div>

              {event.description && (
                <div ref={descRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.8rem', fontSize: '1rem' }}>Descripción del evento</h3>
                  <div style={{ position: 'relative' }}>
                    <p style={{ color: colors.textSecondary, fontSize: '0.9rem', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', maxHeight: descExpanded ? 'none' : '22rem', overflow: 'hidden' }}>
                      {event.description}
                    </p>
                    {!descExpanded && descNeedsTruncation && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '5rem', background: `linear-gradient(to bottom, transparent, ${colors.bgSecondary})` }} />
                    )}
                  </div>
                  {descNeedsTruncation && (
                    <button onClick={() => setDescExpanded(!descExpanded)} style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.accent, borderRadius: '20px', padding: '0.5rem 1.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', marginTop: '1rem' }}>
                      {descExpanded ? 'Ver menos' : 'Saber más'}
                    </button>
                  )}
                </div>
              )}

              {videoIds.length > 0 && (
                <div ref={videosRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ color: colors.text, fontWeight: 'bold', fontSize: '1rem', margin: 0 }}>Galería de vídeos</h3>
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => setVideoPage(p => Math.max(0, p - 1))} disabled={videoPage === 0} style={{ backgroundColor: videoPage === 0 ? colors.bgTertiary : colors.accent, color: videoPage === 0 ? colors.textSecondary : colors.accentText, border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: videoPage === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ArrowBackIosIcon style={{ fontSize: 14 }} />
                        </button>
                        <button onClick={() => setVideoPage(p => Math.min(totalPages - 1, p + 1))} disabled={videoPage === totalPages - 1} style={{ backgroundColor: videoPage === totalPages - 1 ? colors.bgTertiary : colors.accent, color: videoPage === totalPages - 1 ? colors.textSecondary : colors.accentText, border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: videoPage === totalPages - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ArrowForwardIosIcon style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${perView}, 1fr)`, gap: '0.8rem' }}>
                    {visibleVideoIds.map((videoId, i) => (
                      <div key={i} onClick={() => setPlayingVideoId(videoId)} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', aspectRatio: '16/9', backgroundColor: '#000' }}>
                        <img src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} alt="video" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ backgroundColor: 'rgba(255,0,0,0.9)', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PlayArrowIcon style={{ color: '#fff', fontSize: 28 }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {event.map_url && (
                <div ref={mapRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: colors.text, fontWeight: 'bold', marginBottom: '1rem', fontSize: '1rem' }}>Ubicación</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem' }}>
                    <LocationOnIcon style={{ fontSize: 18, color: colors.accent, flexShrink: 0 }} />
                    <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>{event.location}</p>
                  </div>
                  <a href={event.map_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', backgroundColor: colors.accent, color: colors.accentText, padding: '0.7rem', borderRadius: '25px', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    <DirectionsIcon style={{ fontSize: 18 }} /> Cómo llegar
                  </a>
                </div>
              )}

              {event.venue_map && (
                <div ref={recintoRef} style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: colors.text, fontWeight: 'bold', marginBottom: '1rem', fontSize: '1rem' }}>Mapa del recinto</h3>
                  <RecintoMap venueMap={event.venue_map} ticketTypes={ticketTypes} />
                </div>
              )}

              <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '1.5rem' }}>
                <h3 style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.8rem', fontSize: '1rem' }}>Información importante</h3>
                <p style={{ color: colors.textSecondary, fontSize: '0.9rem', lineHeight: 1.6 }}>Se requiere presentar el código QR en la entrada del evento. Máximo 8 entradas por usuario.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Event;